import React, { useState, FormEvent } from 'react';
import { useChatbot } from '../../context/ChatbotContext';

interface ChatInputProps {
  isLoading: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ isLoading }) => {
  const [message, setMessage] = useState('');
  const { sendMessage } = useChatbot();
  
  // Handle form submission
  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    
    // Don't send empty messages or when already loading
    if (message.trim() === '' || isLoading) {
      return;
    }
    
    // Send message and clear input
    sendMessage(message);
    setMessage('');
  };
  
  // Suggested questions/prompts for the user to easily click
  const suggestions = [
    "What services do you offer?",
    "How much do your services cost?",
    "How can I contact your team?",
    "Do you offer custom AI solutions?"
  ];
  
  return (
    <div className="border-t border-gray-200 bg-white dark:bg-neutral-800 dark:border-neutral-700">
      {/* Quick suggestions */}
      <div className="p-2 overflow-x-auto whitespace-nowrap flex space-x-2">
        {suggestions.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => {
              if (!isLoading) {
                sendMessage(suggestion);
              }
            }}
            disabled={isLoading}
            className="text-xs bg-gray-100 text-gray-800 py-1 px-2 rounded-full hover:bg-gray-200 transition-colors flex-shrink-0 dark:bg-neutral-700 dark:text-gray-200 dark:hover:bg-neutral-600"
          >
            {suggestion}
          </button>
        ))}
      </div>
      
      {/* Input form */}
      <form onSubmit={handleSubmit} className="p-2 flex items-center space-x-2">
        <input
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isLoading}
          placeholder={isLoading ? "Assistant is typing..." : "Type your message..."}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors dark:bg-neutral-700 dark:border-neutral-600 dark:text-white"
        />
        <button
          type="submit"
          disabled={isLoading || message.trim() === ''}
          className={`p-2 rounded-md bg-primary text-white transition-colors ${
            isLoading || message.trim() === '' 
              ? 'opacity-50 cursor-not-allowed' 
              : 'hover:bg-primary-dark'
          }`}
          aria-label="Send message"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="h-5 w-5" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" 
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default ChatInput;
